/**
 * MessagesPage - Scheduled Messages with clean 3-step flow
 * Features: Recipient selection, Templates, Custom messages, Schedule options
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { customMessageService } from '../../services/api';
import toast from 'react-hot-toast';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';

// Icon component
const Icon = ({ name, size = 16 }) => {
  const icons = {
    plus: <><path d="M5 12h14M12 5v14"/></>,
    x: <><path d="M18 6 6 18M6 6l12 12"/></>,
    trash: <><path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></>,
    send: <><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></>,
    check: <><polyline points="20 6 9 17 4 12"/></>,
    clock: <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></>,
    message: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>,
    sparkles: <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></>,
    calendar: <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    edit: <><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></>,
    arrowLeft: <><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></>,
    image: <><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></>,
    paperclip: <><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name] || icons.message}
    </svg>
  );
};

// Templates mapped to Twilio template SIDs
// Each template has a corresponding approved Twilio template for WhatsApp delivery
const TEMPLATES = [
  {
    id: 'rsvp_invite',
    name: 'RSVP Invite',
    description: 'Send initial invitation with Yes/No buttons',
    message: "Please confirm your attendance for {{eventName}} on {{eventDate}} by choosing an option below. Thank you!",
    templateSid: 'HXe43d7f5de8cff019baa181f8ca1d5a2d', // RSVP Quick Reply with buttons
    hasButtons: true,
  },
  {
    id: 'rsvp_reminder',
    name: 'RSVP Reminder',
    description: 'Remind guests who haven\'t responded',
    message: "Friendly reminder about {{eventName}} on {{eventDate}}! We'd love to know if you can make it.",
    templateSid: 'HXeec2ce90c55fd885df4c01f94bd633d9', // RSVP Reminder Quick Reply
    hasButtons: true,
  },
  {
    id: 'save_the_date',
    name: 'Save the Date',
    description: 'Early announcement to guests',
    message: "Save the Date! {{eventName}} is happening on {{eventDate}} at {{venue}}. Formal invitation to follow!",
    templateSid: 'HXd8cffaf4c58ce0713b9a71cceb2d67df', // Save the Date template
    hasButtons: false,
  },
];

// Audience options - must match GuestsPage filters exactly
// Note: Default rsvp in database is 'unassigned'
const AUDIENCES = [
  { id: 'all', label: 'All Guests', filter: () => true },
  { id: 'notinvited', label: 'Not Invited', filter: g => g.rsvp === 'unassigned' },
  { id: 'pending', label: 'Pending', filter: g => g.rsvp === 'pending' },
  { id: 'confirmed', label: 'Confirmed', filter: g => g.rsvp === 'yes' },
  { id: 'declined', label: 'Declined', filter: g => g.rsvp === 'no' },
];

// Schedule options
const SCHEDULES = [
  { id: 'now', label: 'Send Now', icon: 'send', description: 'Message will be sent immediately' },
  { id: 'scheduled', label: 'Schedule', icon: 'calendar', description: 'Pick a date and time' },
];

// ============ MESSAGES PAGE ============
function MessagesPage({ event, onRefresh, eventId }) {
  const [messageBatches, setMessageBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // New message flow state
  const [showComposer, setShowComposer] = useState(false);
  const [step, setStep] = useState(1);
  const [audience, setAudience] = useState('all');
  const [templateId, setTemplateId] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [schedule, setSchedule] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  // Media upload state
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const guests = event?.guests || [];

  // Helper to show sample message with substituted variables
  const getPreviewMessage = (template) => {
    const eventName = event?.name || 'Your Event';
    const eventDate = event?.startDate
      ? new Date(event.startDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
      : 'Event Date';
    // Use host names or a sample guest name
    const sampleName = event?.brideName || event?.groomName || 'Guest';

    return template
      .replace(/\{\{name\}\}/gi, sampleName)
      .replace(/\{\{eventName\}\}/gi, eventName)
      .replace(/\{\{eventDate\}\}/gi, eventDate);
  };

  // Calculate audience counts
  const audienceCounts = useMemo(() => {
    return AUDIENCES.reduce((acc, aud) => {
      acc[aud.id] = guests.filter(g => g.whatsappNumber && aud.filter(g)).length;
      return acc;
    }, {});
  }, [guests]);

  // Fetch message batches
  const fetchBatches = async () => {
    try {
      const data = await customMessageService.getMessageBatches(eventId);
      setMessageBatches(data.batches || []);
    } catch (err) {
      console.error('Error fetching message batches:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, [eventId]);

  // Fetch batch detail (per-recipient)
  const openBatchDetail = async (batch) => {
    setSelectedBatch(batch);
    setLoadingDetails(true);
    try {
      const data = await customMessageService.getBatchDetails(batch.id);
      setBatchDetails(data);
    } catch (err) {
      console.error('Error fetching batch details:', err);
      toast.error('Failed to load details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeBatchDetail = () => {
    setSelectedBatch(null);
    setBatchDetails(null);
  };

  // Poll every 15s when there are active/pending batches
  useEffect(() => {
    const hasActive = messageBatches.some(b =>
      b.jobStats?.pending > 0 || b.jobStats?.awaitingReply > 0
    );
    if (hasActive && !showComposer) {
      const interval = setInterval(fetchBatches, 15000);
      return () => clearInterval(interval);
    }
  }, [messageBatches, showComposer, eventId]);

  // Reset composer
  const resetComposer = () => {
    setShowComposer(false);
    setStep(1);
    setAudience('all');
    setTemplateId(null);
    setCustomMessage('');
    setSchedule('now');
    setScheduledDate('');
    setScheduledTime('');
    // Reset media state
    setMediaFile(null);
    setMediaPreview(null);
    setMediaUrl(null);
  };

  // Handle media file selection
  const handleMediaSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'video/mp4'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('File type not supported. Use JPG, PNG, WebP, PDF, or MP4.');
      return;
    }

    // Validate file size (16MB max for video, 5MB for images)
    const maxSize = file.type.startsWith('video/') ? 16 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${file.type.startsWith('video/') ? '16MB' : '5MB'}.`);
      return;
    }

    setMediaFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setMediaPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setMediaPreview(null);
    }

    // Upload to R2
    setUploadingMedia(true);
    try {
      const result = await customMessageService.uploadMedia(file);
      setMediaUrl(result.mediaUrl);
      toast.success('Media uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
      setMediaFile(null);
      setMediaPreview(null);
    } finally {
      setUploadingMedia(false);
    }
  };

  // Remove uploaded media
  const removeMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Get selected template
  const selectedTemplate = TEMPLATES.find(t => t.id === templateId);
  const messageContent = templateId === 'custom' ? customMessage : selectedTemplate?.message || '';

  // Get recipient count
  const recipientCount = audienceCounts[audience] || 0;

  // Send message
  const sendMessage = async () => {
    // Allow sending with just media OR message (or both)
    if (!messageContent.trim() && !mediaUrl) {
      toast.error('Please write a message or attach media');
      return;
    }
    if (recipientCount === 0) {
      toast.error('No recipients match the selected criteria');
      return;
    }

    setSaving(true);
    try {
      const audienceFilter = AUDIENCES.find(a => a.id === audience)?.filter || (() => true);
      const recipients = guests.filter(g => g.whatsappNumber && audienceFilter(g));

      if (schedule === 'now') {
        // All predefined templates use Twilio template SIDs for proper delivery
        // Only custom messages use freeform text
        const isCustom = templateId === 'custom';

        if (!isCustom && selectedTemplate?.templateSid) {
          // Use bulk send endpoint for ALL predefined templates (proper Twilio template)
          await customMessageService.sendRsvpMessages(
            eventId,
            recipients.map(g => g._id),
            templateId,
            true // sendImmediately
          );
          toast.success(`${selectedTemplate.name} sent to ${recipients.length} guests`);
        } else {
          // Use custom message endpoint ONLY for custom text
          await customMessageService.sendCustomMessage(eventId, {
            recipients: recipients.map(g => ({
              _id: g._id,
              name: g.name,
              whatsappNumber: g.whatsappNumber,
            })),
            message: messageContent,
            mediaUrl: mediaUrl || undefined, // Include media if uploaded
          });
          toast.success(`Custom message sent to ${recipients.length} guests`);
        }
      } else {
        // Scheduled message - create jobs directly instead of alert rules
        // This ensures messages are sent at the exact scheduled time
        const audienceFilter = AUDIENCES.find(a => a.id === audience)?.filter || (() => true);
        const recipients = guests.filter(g => g.whatsappNumber && audienceFilter(g));
        const isCustom = templateId === 'custom';

        // Calculate scheduled date/time
        let scheduledFor = new Date();
        if (scheduledDate && scheduledTime) {
          scheduledFor = new Date(`${scheduledDate}T${scheduledTime}`);
        } else if (scheduledDate) {
          scheduledFor = new Date(`${scheduledDate}T09:00`);
        }

        // Validate scheduled date is selected and in the future
        if (schedule === 'scheduled') {
          if (!scheduledDate) {
            toast.error('Please select a date for the scheduled message');
            setSaving(false);
            return;
          }
          if (scheduledFor <= new Date()) {
            toast.error('Please select a future date and time');
            setSaving(false);
            return;
          }
        }

        await customMessageService.sendCustomMessage(eventId, {
          recipients: recipients.map(g => ({
            _id: g._id,
            name: g.name,
            whatsappNumber: g.whatsappNumber,
          })),
          message: messageContent,
          mediaUrl: mediaUrl || undefined,
          templateSid: isCustom ? null : selectedTemplate?.templateSid,
          scheduledFor: scheduledFor.toISOString(),
        });
        toast.success(schedule === 'now' ? 'Message sent!' : `Message scheduled for ${scheduledFor.toLocaleString()}`);
        fetchBatches();
      }
      resetComposer();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send message');
    } finally {
      setSaving(false);
    }
  };

  // Cancel/delete a message batch
  const cancelBatch = async (batch) => {
    try {
      await customMessageService.cancelBatch(batch.id);
      toast.success('Message batch cancelled');
      setDeleteTarget(null);
      fetchBatches();
    } catch (err) {
      toast.error('Failed to cancel batch');
    }
  };

  // Format date
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  // Stepper styles
  const stepStyle = (s) => ({
    width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, fontWeight: 600, flexShrink: 0,
    background: step >= s ? 'var(--c-primary)' : 'var(--c-muted)',
    color: step >= s ? '#fff' : 'var(--c-fg-muted)',
  });

  const stepLabelStyle = (s) => ({
    fontSize: 13, fontWeight: step === s ? 600 : 400,
    color: step >= s ? 'var(--c-fg)' : 'var(--c-fg-muted)',
  });

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600 }}>Messages</h2>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--c-fg-muted)' }}>Send messages to your guests via WhatsApp</p>
        </div>
        {!showComposer && (
          <button className="btn-primary" onClick={() => setShowComposer(true)}>
            <Icon name="plus" size={14} /> New Message
          </button>
        )}
      </div>

      {/* Composer Panel */}
      {showComposer && (
        <div className="panel" style={{ marginBottom: 24 }}>
          <div className="panel-body" style={{ padding: 0 }}>
            {/* Stepper */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 24px', borderBottom: '1px solid var(--c-border)' }}>
              <div style={stepStyle(1)}>{step > 1 ? <Icon name="check" size={14} /> : '1'}</div>
              <div style={{ flex: 1 }}>
                <div style={stepLabelStyle(1)}>Recipients</div>
              </div>
              <div style={{ width: 40, height: 2, background: step > 1 ? 'var(--c-primary)' : 'var(--c-border)', borderRadius: 1 }} />
              <div style={stepStyle(2)}>{step > 2 ? <Icon name="check" size={14} /> : '2'}</div>
              <div style={{ flex: 1 }}>
                <div style={stepLabelStyle(2)}>Message</div>
              </div>
              <div style={{ width: 40, height: 2, background: step > 2 ? 'var(--c-primary)' : 'var(--c-border)', borderRadius: 1 }} />
              <div style={stepStyle(3)}>3</div>
              <div style={{ flex: 1 }}>
                <div style={stepLabelStyle(3)}>Schedule</div>
              </div>
            </div>

            {/* Step 1: Recipients */}
            {step === 1 && (
              <div style={{ padding: 24 }}>
                <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Who should receive this message?</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                  {AUDIENCES.map(aud => (
                    <div
                      key={aud.id}
                      onClick={() => setAudience(aud.id)}
                      style={{
                        padding: 16, borderRadius: 12, cursor: 'pointer',
                        border: audience === aud.id ? '2px solid var(--c-primary)' : '2px solid var(--c-border)',
                        background: audience === aud.id ? 'hsl(170 60% 25% / 0.05)' : '#fff',
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{aud.label}</div>
                      <div style={{ fontSize: 13, color: 'var(--c-fg-muted)' }}>{audienceCounts[aud.id]} guests</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
                  <button className="btn-secondary" onClick={resetComposer}>Cancel</button>
                  <button className="btn-primary" onClick={() => setStep(2)} disabled={recipientCount === 0}>
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Message */}
            {step === 2 && (
              <div style={{ padding: 24 }}>
                <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Choose a template or write custom</h4>

                {/* Templates */}
                <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
                  {TEMPLATES.map(tpl => (
                    <div
                      key={tpl.id}
                      onClick={() => { setTemplateId(tpl.id); setCustomMessage(''); }}
                      style={{
                        padding: 16, borderRadius: 12, cursor: 'pointer',
                        border: templateId === tpl.id ? '2px solid var(--c-primary)' : '2px solid var(--c-border)',
                        background: templateId === tpl.id ? 'hsl(170 60% 25% / 0.05)' : '#fff',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{tpl.name}</div>
                          <div style={{ fontSize: 13, color: 'var(--c-fg-muted)' }}>{tpl.description}</div>
                        </div>
                        {templateId === tpl.id && (
                          <div style={{ color: 'var(--c-primary)' }}><Icon name="check" size={18} /></div>
                        )}
                      </div>
                      {templateId === tpl.id && (
                        <div style={{
                          marginTop: 12, padding: 12, borderRadius: 8,
                          background: 'var(--c-muted)', fontSize: 13, color: 'var(--c-fg-muted)', lineHeight: 1.5
                        }}>
                          {getPreviewMessage(tpl.message)}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Custom Message Option */}
                  <div
                    onClick={() => setTemplateId('custom')}
                    style={{
                      padding: 16, borderRadius: 12, cursor: 'pointer',
                      border: templateId === 'custom' ? '2px solid var(--c-primary)' : '2px solid var(--c-border)',
                      background: templateId === 'custom' ? 'hsl(170 60% 25% / 0.05)' : '#fff',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                          <Icon name="edit" size={14} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                          Custom Message
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--c-fg-muted)' }}>Write your own message</div>
                      </div>
                      {templateId === 'custom' && (
                        <div style={{ color: 'var(--c-primary)' }}><Icon name="check" size={18} /></div>
                      )}
                    </div>
                    {templateId === 'custom' && (
                      <div style={{ marginTop: 12 }} onClick={e => e.stopPropagation()}>
                        <textarea
                          style={{
                            width: '100%', padding: 12, borderRadius: 8,
                            border: '1px solid var(--c-border)', fontSize: 14, outline: 'none',
                            fontFamily: 'inherit', minHeight: 100, resize: 'vertical'
                          }}
                          placeholder="Type your message here... Use {{guestName}} to personalize"
                          value={customMessage}
                          onChange={e => setCustomMessage(e.target.value)}
                        />

                        {/* Media Upload */}
                        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,application/pdf,video/mp4"
                            onChange={handleMediaSelect}
                            style={{ display: 'none' }}
                          />

                          {!mediaFile ? (
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '8px 12px', borderRadius: 8,
                                border: '1px dashed var(--c-border)',
                                background: 'transparent', cursor: 'pointer',
                                fontSize: 13, color: 'var(--c-fg-muted)'
                              }}
                            >
                              <Icon name="paperclip" size={14} />
                              Attach media
                            </button>
                          ) : (
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 12,
                              padding: '8px 12px', borderRadius: 8,
                              background: 'var(--c-muted)', flex: 1
                            }}>
                              {mediaPreview ? (
                                <img
                                  src={mediaPreview}
                                  alt="Preview"
                                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }}
                                />
                              ) : (
                                <div style={{
                                  width: 40, height: 40, borderRadius: 6,
                                  background: 'var(--c-border)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                  <Icon name="paperclip" size={16} />
                                </div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {mediaFile.name}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--c-fg-muted)' }}>
                                  {uploadingMedia ? 'Uploading...' : `${(mediaFile.size / 1024).toFixed(1)} KB`}
                                </div>
                              </div>
                              {!uploadingMedia && (
                                <button
                                  type="button"
                                  onClick={removeMedia}
                                  style={{
                                    background: 'transparent', border: 'none', cursor: 'pointer',
                                    color: 'var(--c-fg-muted)', padding: 4
                                  }}
                                >
                                  <Icon name="x" size={16} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        <div style={{ fontSize: 11, color: 'var(--c-fg-muted)', marginTop: 8 }}>
                          Supported: JPG, PNG, WebP, PDF, MP4 (max 5MB images, 16MB video)
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', marginTop: 24 }}>
                  <button className="btn-secondary" onClick={() => setStep(1)}>
                    <Icon name="arrowLeft" size={14} /> Back
                  </button>
                  <button className="btn-primary" onClick={() => setStep(3)} disabled={!messageContent.trim() && !mediaUrl}>
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Schedule */}
            {step === 3 && (
              <div style={{ padding: 24 }}>
                <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>When should this be sent?</h4>

                <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
                  {SCHEDULES.map(sch => (
                    <div
                      key={sch.id}
                      onClick={() => setSchedule(sch.id)}
                      style={{
                        padding: 16, borderRadius: 12, cursor: 'pointer',
                        border: schedule === sch.id ? '2px solid var(--c-primary)' : '2px solid var(--c-border)',
                        background: schedule === sch.id ? 'hsl(170 60% 25% / 0.05)' : '#fff',
                        display: 'flex', alignItems: 'center', gap: 16,
                      }}
                    >
                      <div style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: schedule === sch.id ? 'var(--c-primary)' : 'var(--c-muted)',
                        color: schedule === sch.id ? '#fff' : 'var(--c-fg-muted)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <Icon name={sch.icon} size={18} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{sch.label}</div>
                        <div style={{ fontSize: 13, color: 'var(--c-fg-muted)' }}>{sch.description}</div>
                      </div>
                      {schedule === sch.id && (
                        <div style={{ color: 'var(--c-primary)' }}><Icon name="check" size={18} /></div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Date/Time picker for scheduled */}
                {schedule !== 'now' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--c-fg-muted)' }}>
                        Date {schedule === 'scheduled' && <span style={{ color: 'var(--c-destructive)' }}>*</span>}
                      </label>
                      <input
                        type="date"
                        value={scheduledDate}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={e => setScheduledDate(e.target.value)}
                        required={schedule === 'scheduled'}
                        style={{
                          width: '100%', padding: '10px 12px',
                          border: schedule === 'scheduled' && !scheduledDate ? '1px solid var(--c-destructive)' : '1px solid var(--c-border)',
                          borderRadius: 8, fontSize: 14, outline: 'none'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--c-fg-muted)' }}>Time</label>
                      <input
                        type="time"
                        value={scheduledTime}
                        onChange={e => setScheduledTime(e.target.value)}
                        placeholder="09:00"
                        style={{
                          width: '100%', padding: '10px 12px', border: '1px solid var(--c-border)',
                          borderRadius: 8, fontSize: 14, outline: 'none'
                        }}
                      />
                      <div style={{ fontSize: 11, color: 'var(--c-fg-muted)', marginTop: 4 }}>Default: 9:00 AM</div>
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div style={{
                  padding: 16, borderRadius: 12, background: 'var(--c-muted)', marginBottom: 24
                }}>
                  <div style={{ fontSize: 13, color: 'var(--c-fg-muted)', marginBottom: 8 }}>Summary</div>
                  <div style={{ fontSize: 14 }}>
                    <strong>{recipientCount} recipients</strong> will receive <strong>{selectedTemplate?.name || 'Custom Message'}</strong>
                    {schedule === 'now' && ' immediately'}
                    {schedule === 'scheduled' && scheduledDate && ` on ${formatDate(scheduledDate + 'T' + (scheduledTime || '09:00'))}`}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
                  <button className="btn-secondary" onClick={() => setStep(2)}>
                    <Icon name="arrowLeft" size={14} /> Back
                  </button>
                  <button className="btn-primary" onClick={sendMessage} disabled={saving}>
                    <Icon name="send" size={14} /> {saving ? 'Sending...' : schedule === 'now' ? 'Send Now' : 'Schedule'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Message Batches List */}
      {!showComposer && (
        <>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-fg-muted)' }}>Loading...</div>
          ) : messageBatches.length === 0 ? (
            <div className="panel">
              <div className="panel-body" style={{ padding: 60, textAlign: 'center' }}>
                <div style={{ width: 80, height: 80, margin: '0 auto 20px', borderRadius: '50%', background: 'var(--c-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="message" size={32} />
                </div>
                <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>No messages yet</h3>
                <p style={{ color: 'var(--c-fg-muted)', marginBottom: 24, maxWidth: 300, margin: '0 auto 24px' }}>
                  Send RSVP invites, reminders, or custom messages to your guests.
                </p>
                <button className="btn-primary" onClick={() => setShowComposer(true)}>
                  <Icon name="plus" size={14} /> New Message
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {messageBatches.map(batch => {
                const sent = batch.deliveryStats?.sent || 0;
                const delivered = batch.deliveryStats?.delivered || 0;
                const read = batch.deliveryStats?.read || 0;
                const deliveredTotal = delivered + read;
                const failedDelivery = (batch.deliveryStats?.failed || 0) + (batch.deliveryStats?.undelivered || 0);
                const failedJobs = batch.jobStats?.failed || 0;
                const failed = failedDelivery + failedJobs;
                const pending = batch.jobStats?.pending || 0;
                const completed = batch.jobStats?.completed || 0;
                const allQueued = pending === batch.totalRecipients && completed === 0;
                const isScheduled = batch.scheduledFor && new Date(batch.scheduledFor) > new Date();
                const noBadges = !allQueued && deliveredTotal === 0 && read === 0 && pending === 0 && failed === 0;

                const badgeStyle = (bg, fg, dot) => ({
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '3px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 500,
                  background: bg, color: fg
                });
                const dotStyle = (bg) => ({ width: 6, height: 6, borderRadius: '50%', background: bg });

                return (
                  <div key={batch.id} className="panel" style={{ cursor: 'pointer' }} onClick={() => openBatchDetail(batch)}>
                    <div className="panel-body" style={{ padding: '16px 20px' }}>
                      {/* Row 1: Label + timestamp + cancel */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{
                            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                            background: isScheduled ? 'hsl(210 80% 92%)' : 'var(--c-primary)',
                            color: isScheduled ? 'hsl(210 70% 45%)' : '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <Icon name={isScheduled ? 'clock' : 'send'} size={18} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{batch.label}</div>
                            <div style={{ fontSize: 12, color: 'var(--c-fg-muted)', marginTop: 2 }}>
                              {batch.totalRecipients} {batch.totalRecipients === 1 ? 'recipient' : 'recipients'}
                              {isScheduled
                                ? ` · Scheduled ${formatDate(batch.scheduledFor)}`
                                : ` · ${formatDate(batch.createdAt)}`}
                            </div>
                          </div>
                        </div>
                        {batch.canCancel && (
                          <button
                            className="btn-secondary"
                            style={{ padding: '6px 12px', fontSize: 12, color: 'var(--c-destructive)' }}
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(batch); }}
                          >
                            Cancel
                          </button>
                        )}
                      </div>

                      {/* Row 2: Delivery stat badges — always visible */}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginLeft: 52 }}>
                        {allQueued && (
                          <span style={badgeStyle('hsl(200 15% 93%)', 'hsl(200 10% 40%)')}>
                            <span style={dotStyle('hsl(200 10% 60%)')} />
                            {batch.totalRecipients} Queued
                          </span>
                        )}
                        {noBadges && completed > 0 && (
                          <span style={badgeStyle('hsl(140 50% 92%)', 'hsl(140 45% 30%)')}>
                            <span style={dotStyle('hsl(140 60% 45%)')} />
                            {completed} Sent
                          </span>
                        )}
                        {deliveredTotal > 0 && (
                          <span style={badgeStyle('hsl(140 50% 92%)', 'hsl(140 45% 30%)')}>
                            <span style={dotStyle('hsl(140 60% 45%)')} />
                            {deliveredTotal} Delivered
                          </span>
                        )}
                        {read > 0 && (
                          <span style={badgeStyle('hsl(170 50% 90%)', 'hsl(170 55% 25%)')}>
                            <span style={dotStyle('var(--c-primary)')} />
                            {read} Read
                          </span>
                        )}
                        {!allQueued && pending > 0 && (
                          <span style={badgeStyle('hsl(210 80% 93%)', 'hsl(210 70% 40%)')}>
                            <span style={dotStyle('hsl(210 70% 55%)')} />
                            {pending} Pending
                          </span>
                        )}
                        {sent > 0 && deliveredTotal === 0 && (
                          <span style={badgeStyle('hsl(45 80% 92%)', 'hsl(45 60% 30%)')}>
                            <span style={dotStyle('hsl(45 70% 50%)')} />
                            {sent} In Transit
                          </span>
                        )}
                        {failed > 0 && (
                          <span style={badgeStyle('hsl(0 60% 93%)', 'hsl(0 55% 40%)')}>
                            <span style={dotStyle('hsl(0 60% 50%)')} />
                            {failed} Failed
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <DeleteConfirmationModal
        show={!!deleteTarget}
        onConfirm={() => cancelBatch(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        title="Cancel Message Batch"
        message={`Cancel sending to ${deleteTarget?.totalRecipients || 0} recipients? Messages already delivered will not be affected.`}
        confirmText="Cancel Batch"
        itemType="message batch"
      />

      {/* Batch Detail Modal */}
      {selectedBatch && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20
        }} onClick={closeBatchDetail}>
          <div style={{
            background: '#fff', borderRadius: 16, width: '100%', maxWidth: 540,
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{
              padding: '20px 24px 16px', borderBottom: '1px solid hsl(40 15% 90%)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{selectedBatch.label}</h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--c-fg-muted)' }}>
                  {selectedBatch.totalRecipients} {selectedBatch.totalRecipients === 1 ? 'recipient' : 'recipients'} · {formatDate(selectedBatch.createdAt)}
                </p>
              </div>
              <button onClick={closeBatchDetail} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--c-fg-muted)'
              }}>
                <Icon name="x" size={20} />
              </button>
            </div>

            {/* Recipient List */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loadingDetails ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-fg-muted)' }}>Loading...</div>
              ) : batchDetails?.recipients?.length > 0 ? (
                <div style={{ padding: '8px 0' }}>
                  {batchDetails.recipients.map((r, i) => {
                    const statusConfig = {
                      read:        { label: 'Read',       bg: 'hsl(170 50% 90%)', fg: 'hsl(170 55% 25%)', dot: 'var(--c-primary)' },
                      delivered:   { label: 'Delivered',  bg: 'hsl(140 50% 92%)', fg: 'hsl(140 45% 30%)', dot: 'hsl(140 60% 45%)' },
                      sent:        { label: 'Sent',       bg: 'hsl(45 80% 92%)',  fg: 'hsl(45 60% 30%)',  dot: 'hsl(45 70% 50%)' },
                      pending:     { label: 'Pending',    bg: 'hsl(210 80% 93%)', fg: 'hsl(210 70% 40%)', dot: 'hsl(210 70% 55%)' },
                      failed:      { label: 'Failed',     bg: 'hsl(0 60% 93%)',   fg: 'hsl(0 55% 40%)',   dot: 'hsl(0 60% 50%)' },
                      undelivered: { label: 'Failed',     bg: 'hsl(0 60% 93%)',   fg: 'hsl(0 55% 40%)',   dot: 'hsl(0 60% 50%)' },
                      cancelled:   { label: 'Cancelled',  bg: 'hsl(0 0% 93%)',    fg: 'hsl(0 0% 45%)',    dot: 'hsl(0 0% 60%)' }
                    };

                    // Use delivery status if available, else derive from job status
                    let displayStatus = r.deliveryStatus || r.jobStatus;
                    if (r.jobStatus === 'completed' && !r.deliveryStatus) displayStatus = 'sent';
                    if (['pending', 'retry_pending', 'rate_limited', 'waiting_opt_in'].includes(r.jobStatus) && !r.deliveryStatus) displayStatus = 'pending';
                    if (['failed', 'permanently_failed'].includes(r.jobStatus)) displayStatus = 'failed';

                    const sc = statusConfig[displayStatus] || statusConfig.pending;
                    const initials = r.guestName ? r.guestName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '??';

                    return (
                      <div key={r.jobId || i} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 24px',
                        borderBottom: i < batchDetails.recipients.length - 1 ? '1px solid hsl(40 15% 94%)' : 'none'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                            background: 'hsl(40 15% 92%)', color: 'var(--c-fg-muted)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 13, fontWeight: 600
                          }}>
                            {initials}
                          </div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500 }}>{r.guestName || 'Unknown'}</div>
                            <div style={{ fontSize: 12, color: 'var(--c-fg-muted)' }}>{r.phone}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '3px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 500,
                            background: sc.bg, color: sc.fg
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.dot }} />
                            {sc.label}
                          </span>
                          {r.readAt && (
                            <span style={{ fontSize: 11, color: 'var(--c-fg-muted)' }}>
                              {new Date(r.readAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-fg-muted)' }}>No recipient data found</div>
              )}
            </div>

            {/* Footer with message preview */}
            {batchDetails?.messagePreview && (
              <div style={{
                padding: '12px 24px', borderTop: '1px solid hsl(40 15% 90%)',
                fontSize: 12, color: 'var(--c-fg-muted)', lineHeight: 1.5
              }}>
                <strong style={{ color: 'var(--c-fg)' }}>Message:</strong> {batchDetails.messagePreview}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default MessagesPage;
